import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../apis/ApiInstence";

const ContactUs = () => {
  const navigate = useNavigate();

  const contactDetails = {
    phone: "+91 9238264606",
    phoneHref: "tel:+919238264606",
    email: "info@pccarecomputers.com",
    emailHref: "mailto:info@pccarecomputers.com",
    address:
      "Pc Care Computer, 1035 Malviya Chowk, Infront Of Yatayat Thana, Jabalpur (Mp) 482001",
    mapHref: "https://www.google.com/maps?q=23.168639,79.931806",
  };

  const socialLinks = [
    {
      label: "Facebook",
      href: "https://www.facebook.com/pccarejabalpur/",
      icon: "fab fa-facebook-f",
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/pccare_jabalpur/",
      icon: "fab fa-instagram",
    },
    {
      label: "YouTube",
      href: "https://www.youtube.com/@pccare_jabalpur",
      icon: "fab fa-youtube",
    },
  ];

  const contactCards = [
    {
      title: "Main Office",
      value: contactDetails.address,
      href: contactDetails.mapHref,
      icon: "fas fa-map-marker-alt",
    },
    {
      title: "Phone Number",
      value: contactDetails.phone,
      href: contactDetails.phoneHref,
      icon: "fas fa-phone",
    },
    {
      title: "Email",
      value: contactDetails.email,
      href: contactDetails.emailHref,
      icon: "fas fa-envelope",
    },
    {
      type: "social",
      title: "Social Media",
      icon: "fas fa-hashtag",
      icons: socialLinks,
    },
  ];

  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    subject: "",
    message: "",
  });

  const [toast, setToast] = useState({
    show: false,
    type: "", // success | error
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ================= TOAST AUTO HIDE ================= */
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, type: "", message: "" });
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  /* ================= VALIDATION ================= */
  const showError = (msg) => {
    setToast({ show: true, type: "error", message: msg });
    return false;
  };

  const showSuccess = (msg) => {
    setToast({ show: true, type: "success", message: msg });
  };

  const validateField = (field, value) => {
    const trimmed = value.trim();
    switch (field) {
      case "name":
        if (!trimmed) return "Please enter your name";
        if (trimmed.length < 2) return "Name must be at least 2 characters";
        return "";
      case "phone":
        if (!trimmed) return "Please enter your mobile number";
        if (!/^[6-9]\d{9}$/.test(trimmed))
          return "Enter a valid 10-digit mobile number";
        return "";
      case "address":
        if (!trimmed) return "Please enter your address";
        if (trimmed.length < 5) return "Address must be at least 5 characters";
        return "";
      case "subject":
        if (!trimmed) return "Please enter a subject";
        if (trimmed.length < 3) return "Subject must be at least 3 characters";
        return "";
      case "message":
        if (!trimmed) return "Please describe your problem";
        if (trimmed.length < 10)
          return "Message must be at least 10 characters";
        return "";
      default:
        return "";
    }
  };

  const validate = () => {
    const nextErrors = {};
    const fieldOrder = ["name", "phone", "address", "subject", "message"];
    fieldOrder.forEach((field) => {
      const error = validateField(field, form[field] || "");
      if (error) nextErrors[field] = error;
    });

    setErrors(nextErrors);
    const firstError = fieldOrder.find((field) => nextErrors[field]);
    if (firstError) return showError(nextErrors[firstError]);
    return true;
  };

  /* ================= HANDLERS ================= */
  const handleBlur = (field) => {
    const error = validateField(field, form[field] || "");
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setForm({ ...form, phone: digitsOnly });
      setErrors((prev) => ({
        ...prev,
        phone: validateField("phone", digitsOnly),
      }));
      return;
    }
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;
  if (isSubmitting) return;

  setIsSubmitting(true);
  try {
    await postRequest("/contactus", {
      name: form.name,
      mobile: form.phone, // backend expects "mobile"
      subject: form.subject,
      message: form.message,
      address: form.address,
    });

    showSuccess("Thank you! Our PC Care expert will contact you shortly.");

    setForm({
      name: "",
      phone: "",
      address: "",
      subject: "",
      message: "",
    });
    setErrors({});
  } catch (error) {
    console.error("Contact API Error:", error);
    const apiMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Server error. Please try again later.";
    showError(apiMessage);
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="bg-gradient-to-b from-[#f7f5ff] to-white relative">

      {/* ================= BOTTOM TOAST (RESPONSIVE) ================= */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            className={`
              fixed z-50
              bottom-6
              left-1/2 -translate-x-1/2
              sm:left-auto sm:right-6 sm:translate-x-0
              w-[90%] sm:w-auto
              max-w-md
              px-5 py-4
              rounded-2xl
              shadow-2xl
              text-white
              text-sm sm:text-base
              ${toast.type === "success" ? "bg-[#351F72]" : "bg-red-500"}
            `}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= HEADER ================= */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <button
            onClick={() => navigate("/")}
            className="w-9 h-9 rounded-full border hover:bg-gray-100"
          >
            &larr;
          </button>
          <span>
            Home / <span className="text-[#351F72] font-semibold">Contact</span>
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold text-center text-[#351F72] mt-2">
          Let's Talk
        </h1>
        <p className="text-center text-gray-600 max-w-xl mx-auto text-sm sm:text-base">
          Facing any laptop or desktop issue?
          Our PC Care experts are ready to help you.
        </p>

        {/* CONTACT INFO CARDS */}
        <div className="mt-5 sm:mt-8">
          <div className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible">
            {contactCards.map((card) => (
              <div
                key={card.title}
                className="group relative flex-none w-[150px] sm:w-auto snap-start bg-white rounded-2xl border border-[#351F72]/10 shadow-lg p-3 text-center hover:shadow-xl hover:-translate-y-0.5 transition"
              >
                <span className="absolute inset-x-0 top-0 h-1 bg-[#F3802C]"></span>
                <div className="w-9 h-9 mx-auto rounded-full bg-[#351F72]/10 text-[#351F72] flex items-center justify-center mb-2 group-hover:scale-105 transition">
                  <i className={card.icon}></i>
                </div>
                <p className="text-[11px] tracking-[0.2em] text-gray-500 font-semibold uppercase">
                  {card.title}
                </p>

                {card.type === "social" ? (
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {card.icons.map((item) => (
                      <a
                        key={item.label}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={item.label}
                        className="w-8 h-8 rounded-full bg-white text-[#351F72] border border-[#351F72]/20 flex items-center justify-center hover:bg-[#F3802C] hover:text-white hover:border-[#F3802C] transition"
                      >
                        <i className={item.icon}></i>
                      </a>
                    ))}
                  </div>
                ) : (
                  <a
                    href={card.href}
                    target={card.href.startsWith("http") ? "_blank" : undefined}
                    rel={
                      card.href.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="mt-2 block text-xs sm:text-base text-gray-700 hover:text-[#351F72] transition line-clamp-2 sm:line-clamp-none"
                  >
                    {card.value}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= MAIN SECTION ================= */}
      <section className="pt-4 sm:pt-6 pb-16 sm:pb-20 ">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 bg-white/80 backdrop-blur-xl p-5  rounded-3xl shadow-2xl items-start">

          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            
          >
            <span className="inline-block mb-4 px-4 py-1 text-sm rounded-full bg-[#351F72] text-[#fff] font-semibold">
              PC Care Support
            </span>

            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#351F72] mb-4">
              Having trouble with your  
              <br />
              <span className="text-[#5b3dbd]">Laptop or Desktop?</span>
            </h3>

            <p className="text-gray-600 text-base sm:text-lg mb-6">
              Reach out directly to PC Care experts for clear, honest guidance.
              No call centers. No delays.
            </p>

            <div className="rounded-2xl overflow-hidden shadow-xl">
              <video
                src="/animation.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-[220px] sm:h-[300px] md:h-[360px] lg:h-[410px] object-cover"
              />
            </div>

          </motion.div>

          {/* RIGHT FORM */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            
          >
            <h3 className="text-lg sm:text-xl font-bold text-[#351F72] mb-5 sm:mb-6">
              Get in Touch
            </h3>

            {[
              { name: "name", type: "text", placeholder: "Your name" },
              { name: "phone", type: "tel", placeholder: "Your phone" },
              { name: "address", type: "text", placeholder: "Your address" },
              { name: "subject", type: "text", placeholder: "Your subject" },
            ].map(({ name, type, placeholder }) => (
              <div key={name} className="mb-4">
                <input
                  type={type}
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  onBlur={() => handleBlur(name)}
                  placeholder={placeholder}
                  required
                  aria-invalid={Boolean(errors[name])}
                  className={`w-full border rounded-xl px-4 py-3 text-sm sm:text-base outline-none transition
                focus:border-[#351F72]
                focus:ring-2 focus:ring-[#351F72]/50 ${
                  errors[name]
                    ? "border-red-400"
                    : ""
                }`}
                />
                {errors[name] && (
                  <p className="mt-1 text-xs text-red-500">{errors[name]}</p>
                )}
              </div>
            ))}

            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              onBlur={() => handleBlur("message")}
              rows="4"
              placeholder="Describe your problem"
              required
              aria-invalid={Boolean(errors.message)}
              className="w-full mb-6 border rounded-xl px-4 py-3 text-sm sm:text-base outline-none resize-none transition
              focus:border-[#351F72]
              focus:ring-2 focus:ring-[#351F72]/50"
            />
            {errors.message && (
              <p className="-mt-4 mb-6 text-xs text-red-500">
                {errors.message}
              </p>
            )}

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#351F72] to-[#5b3dbd] text-white py-3.5 rounded-xl font-semibold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </motion.button>
          </motion.form>
        </div>
      </section>
      {/* ================= MAP SECTION ================= */}
<section className="w-full h-[280px] sm:h-[360px] md:h-[420px] mt-8 sm:mt-10">
 <iframe
  title="PC Care Location"
  src="https://www.google.com/maps?q=23.168639,79.931806&output=embed"
  className="w-full h-full border-0"
  loading="lazy"
  referrerPolicy="no-referrer-when-downgrade"
/>
</section>

    </div>
  );
};

export default ContactUs;
